import React, { useState } from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import { StepButtonBar } from './StepButtonBar';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';

const PRIMARY = '#c4122f';
const buildConnectionsList = (
  law: any,
  companyUnitName: string | undefined,
  formatMessage: (descriptor: { id: string }, values?: Record<string, string>) => string,
): string | undefined => {
  const splitToNames = (value?: string) =>
    (value ? value.split(',') : []).map((v) => v.trim()).filter(Boolean);
  const items = [
    ...splitToNames(law?.processes),
    ...splitToNames(law?.aspects),
    ...splitToNames(law?.tags),
    ...(companyUnitName ? [companyUnitName] : []),
  ];
  if (items.length === 0) return undefined;
  const and = formatMessage({ id: 'utils.connections.and' });
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')}${and}${items[items.length - 1]}`;
};

interface DeleteStep3ConfirmProps {
  selectedRowsData: any[];
  selectedCompanyUnit: any;
  onConfirmDelete: () => Promise<void>;
  onBack: () => void;
  onCancel: () => void;
  isFirstStep?: boolean;
}

const DeleteStep3Confirm = ({
  selectedRowsData,
  selectedCompanyUnit,
  onConfirmDelete,
  onBack,
  onCancel,
}: DeleteStep3ConfirmProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const paperPadding = isMobile ? theme.spacing(2) : theme.spacing(3);
  const [isDeleting, setIsDeleting] = useState(false);
  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await onConfirmDelete();
    } catch (error) {
      console.error('Delete failed:', error);
      setIsDeleting(false);
    }
  };
  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {isDeleting && (
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.72)',
          }}
        >
          <CircularProgress size={40} thickness={4} style={{ color: PRIMARY }} />
        </Box>
      )}
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            width: '100%',
            flexShrink: 0,
            padding: paperPadding,
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
          }}
        >
          <Typography
            variant="h6"
            style={{
              fontWeight: 600,
              marginBottom: theme.spacing(2),
              lineHeight: 1.4,
              fontSize: isMobile ? '16px' : '18px',
            }}
          >
            {formatMessage(
              { id: 'law-portal.delete-law.confirm-title' },
              { unit: selectedCompanyUnit?.name ?? '' }
            )}
          </Typography>

          <Box style={{ display: 'flex', flexDirection: 'column' }}>
            {selectedRowsData.map((law: any) => {
              const lawLabel = `${law?.number ?? ''}${law?.number ? ' – ' : ''}${law?.name ?? ''}`;
              const connections = buildConnectionsList(law, selectedCompanyUnit?.name, formatMessage);

              return (
                <Box
                  key={law?.id}
                  style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px 14px' }}
                >
                  <Box style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '40px',
                        height: '40px',
                        backgroundColor: PRIMARY,
                        borderRadius: '6px',
                        flexShrink: 0,
                      }}
                    >
                      <DescriptionOutlinedIcon style={{ color: '#fff', fontSize: '24px' }} />
                    </Box>
                    <Typography
                      variant="body2"
                      style={{ fontWeight: 500, fontSize: '14px', color: '#222' }}
                    >
                      {connections
                        ? formatMessage(
                            { id: 'law-portal.delete-law.confirm-description-with-connections' },
                            { connections, law: lawLabel },
                          )
                        : formatMessage(
                            { id: 'law-portal.delete-law.confirm-description' },
                            { law: lawLabel },
                          )}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
            <Typography
              variant="body2"
              style={{
                marginTop: theme.spacing(1),
                marginLeft: '67px',
                fontWeight: 500,
                color: '#222',
                fontSize: '14px',
              }}
            >
              {formatMessage({ id: 'law-portal.manage.law-list.table.delete.confirmation' })}
            </Typography>
          </Box>
        </div>
      </div>

      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={onBack}
          onNext={handleConfirmDelete}
          nextLabel="law-portal.delete-law.confirm-delete"
          nextDisabled={isDeleting}
          formatMessage={formatMessage}
          showBack={true}
          isDeleteStep={true}
        />
      </Box>
    </div>
  );
};

export default DeleteStep3Confirm;